import React, { useState, useEffect } from 'react';
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Badge,
  Tooltip,
} from '@mui/material';
import {
    HomeRounded as DashboardIcon,
    CalendarTodayOutlined as EventIcon,
    ChecklistRounded as AssignmentIcon,
    ViewKanbanRounded as KanbanIcon,
    GroupsOutlined as PeopleIcon,
    InsertDriveFileOutlined as InsertDriveFileIcon,
    BarChartRounded as AssessmentIcon,
    NotificationsNoneRounded as NotificationsIcon,
    NoteAltRounded as NotesIcon,
    CloudUploadOutlined as ImportIcon,
} from '@mui/icons-material';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import { AuthService } from '../../services/authService';
import './DashboardLayout.css';
import { 
  useUserServiceGetApiUserMe
} from '../../api/generated/queries/queries';
import AddMenuButton from '../Common/AddMenuButton';
import SearchBar from '../Common/SearchBar';
import ErrorNotification, { useErrorNotification } from '../Common/ErrorNotification';
import { setErrorDisplayCallback } from '../../services/errorLogging/GlobalErrorLogger';
import StorageUsageBar from '../Common/StorageUsageBar';

const drawerWidth = 180;
const collapsedDrawerWidth = 60;

const DashboardLayout: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { data: userData } = useUserServiceGetApiUserMe();
  
  
  // Hook do zarządzania powiadomieniami o błędach
  const { currentError, showError, hideError } = useErrorNotification();

  // Get user initials
  const getUserInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return `${first}${last}`;
  };

  // Ustaw callback dla GlobalErrorLogger
  useEffect(() => {
    setErrorDisplayCallback(showError);
  }, [showError]);
    // Sprawdzenie aktywnej ścieżki do podświetlania elementów menu
  const isActiveRoute = (path: string) => location.pathname === path;

  // Automatycznie zamknij szufladę na mobilnych urządzeniach
  React.useEffect(() => {
    if (isMobile) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isMobile]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const navigate = useNavigate();
    const menuItems = [
        { text: 'Pulpit', icon: <DashboardIcon />, path: '/' },
        { text: 'Kalendarz', icon: <EventIcon />, path: '/calendar' },
        { text: 'Zadania', icon: <AssignmentIcon />, path: '/tasks' },
        // { text: 'Kanban', icon: <KanbanIcon />, path: '/tasks/kanban' },
        { text: 'Kontakty', icon: <PeopleIcon />, path: '/leads' },
        { text: 'Notatki', icon: <NotesIcon />, path: '/notes' },
        { text: 'Pliki', icon: <InsertDriveFileIcon />, path: '/files' },
        { text: 'Raporty', icon: <AssessmentIcon />, path: '/reports' },
        { text: 'Importy', icon: <ImportIcon />, path: '/imports' },
    ];

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  const handleLogout = () => {
    AuthService.logout();
    // Po wylogowaniu przekierowanie nastąpi automatycznie
  };

  return (
    <Box className="dashboard-layout">
      <CssBaseline />

      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          width: '100%',
          left: 0,
          right: 0,
          height: 48, // zmniejszona wysokość
          background: 'linear-gradient(to right, #06b6d4, #2563eb)',
          borderRadius: 0,
          justifyContent: 'center', // wyśrodkowanie pionowe zawartości
          boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)' // dodanie lekkiego cienia
        }}
      >
        <Toolbar >
          <Tooltip title={open ? "Zwiń menu" : "Rozwiń menu"}>
            <IconButton
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                marginRight: 2,
                bgcolor: 'white',
                borderRadius: '50%',
                padding: '4px',
                '&:hover': {
                  bgcolor: 'white',
                }
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient id="ustalone-logo-grad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#06b6d4" />
                    <stop offset="1" stopColor="#2563eb" />
                  </linearGradient>
                </defs>
                <circle cx="20" cy="20" r="18" fill="url(#ustalone-logo-grad)" />
                <path
                  d="M14 21.5L19 26L27 16"
                  stroke="white"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </IconButton>
          </Tooltip>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            ustalone
          </Typography>

            <Box sx={{ flexGrow: 1, maxWidth: 400 }}>
                <SearchBar />
            </Box>

            <AddMenuButton />

         {/* <IconButton color="inherit">
            <Badge badgeContent={4} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>*/}



          <IconButton
            edge="end"
            aria-label="account of current user"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
          >
            <Avatar sx={{ 
              width: 32, 
              height: 32, 
              bgcolor: userData?.color || 'primary.dark',
              fontSize: '0.875rem',
              fontWeight: 600
            }}>
              {getUserInitials(userData?.firstName, userData?.lastName)}
            </Avatar>
          </IconButton>

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => {
              handleMenuClose();
              handleNavClick('/my-account');
            }}>Moje konto</MenuItem>
            {userData?.roles?.includes('api-admin') && (
              <MenuItem onClick={() => {
                handleMenuClose();
                handleNavClick('/settings');
              }}>Ustawienia</MenuItem>
            )}
            <MenuItem onClick={() => {
              handleMenuClose();
              handleLogout();
            }}>Wyloguj</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
        <Drawer
            className="dashboard-drawer"
            variant={isMobile ? 'temporary' : 'permanent'}
            open={open}
            onClose={handleDrawerToggle}
            sx={{
                width: open ? drawerWidth : collapsedDrawerWidth,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    position: 'relative',
                    whiteSpace: 'nowrap',
                    width: open ? drawerWidth : collapsedDrawerWidth,
                    overflow: 'hidden',
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    boxSizing: 'border-box',
                    boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.1), 0px 4px 5px 0px rgba(0,0,0,0.07), 0px 1px 10px 0px rgba(0,0,0,0.06)',
                    ...(!open && {
                        overflowX: 'hidden',
                        transition: theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                    }),
                },
            }}
        >
            <Toolbar />
            <Box sx={{ overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', height: 'calc(100% - 24px)' }}>
                <List sx={{ width: open ? drawerWidth : collapsedDrawerWidth }}>
                    {menuItems.map((item) => {
                        const isActive = isActiveRoute(item.path);

                        const listItem = (
                            <ListItem
                                key={item.text}
                                onClick={() => handleNavClick(item.path)}
                                sx={{
                                    position: 'relative',
                                    cursor: 'pointer',
                                    bgcolor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                    '&:hover': {
                                        bgcolor: isActive ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.03)'
                                    },
                                    justifyContent: open ? 'initial' : 'center',
                                    px: open ? 2 : 1,
                                    py: 1.5,
                                    '&::before': isActive
                                        ? {
                                            content: '""',
                                            position: 'absolute',
                                            left: 0,
                                            top: 6,
                                            bottom: 6,
                                            width: 3,
                                            borderRadius: '0 3px 3px 0',
                                            background: 'linear-gradient(to bottom, #06b6d4, #2563eb)',
                                        }
                                        : {},
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: open ? 32 : 'auto',
                                        justifyContent: 'center',
                                        '& svg': {
                                            fontSize: 18, // 👈 dodaje fontSize do wszystkich ikon SVG w elemencie
                                        },
                                        color: isActive ? '#2563eb' : 'text.secondary',
                                        filter: isActive ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' : 'none',
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                                {open && (
                                    <ListItemText
                                        primary={item.text}
                                        primaryTypographyProps={{
                                            fontSize: 12,
                                            fontWeight: isActive ? 600 : 400,
                                            color: isActive ? '#4B5563' : 'text.secondary',
                                        }}
                                    />
                                )}
                            </ListItem>
                        );

                        return open ? (
                            <React.Fragment key={item.text}>
                                {listItem}
                            </React.Fragment>
                        ) : (
                            <Tooltip key={item.text} title={item.text} placement="right">
                                {listItem}
                            </Tooltip>
                        );
                    })}
                </List>

                {/* Storage Usage Bar */}
                <Box sx={{ mt: 'auto' }}>
                    <StorageUsageBar collapsed={!open} />
                </Box>

                {open && (
                    <Box 
                        sx={{ 
                            p: 2, 
                            textAlign: 'center', 
                            width: drawerWidth,
                            cursor: 'pointer'
                        }}
                        onClick={() => handleNavClick('/my-account')}
                    >
                        <Typography
                            variant="body2"
                            fontSize={13}
                            fontWeight={500}
                            color="text.secondary"
                            sx={{
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline',
                                    color: 'primary.main'
                                }
                            }}
                        >
                            {userData?.firstName} {userData?.lastName}
                        </Typography>
                    </Box>
                )}
                {open && (
                    <Box sx={{ textAlign: 'center', width: drawerWidth, pb: 2 }}>
                        <Typography
                            variant="body2"
                            fontSize={13}
                            component="a"
                            href="https://ustalone.com/pomoc"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: 'text.secondary',
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline',
                                    color: 'primary.main'
                                }
                            }}
                        >
                            Pomoc
                        </Typography>
                    </Box>
                )}
            </Box>
        </Drawer>

        {/* Main Content */}
      <Box
        component="main"
        role="main"
        aria-label="Główna zawartość aplikacji"
        className="dashboard-main"
        data-mui-color-scheme={theme.palette.mode}
        sx={{
          flexGrow: 1,
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
          backgroundColor: 'linear-gradient(to bottom, #ffffff, #f0f8ff)',
        }}
      >
        <Toolbar /> {/* Offset dla AppBar */}
        <Box
          className="dashboard-content"
          sx={{
            flexGrow: 1
          }}
        >
          <Outlet />
        </Box>
      </Box>
      
      {/* Globalne powiadomienia o błędach */}
      <ErrorNotification 
        error={currentError} 
        onClose={hideError} 
      />
    </Box>
  );
};

export default DashboardLayout;
